import { useEffect, type ReactElement } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import track from 'c9-tracking';
import { SnackbarProvider } from 'notistack';

import { AppContextProvider } from 'context/AppContext';
import { ConfigContextProvider, TConfig } from 'context/ConfigContext';
import { LoggerContextProvider } from 'context/LoggerContext';

import AppLayout from 'layouts/AppLayout';
import AddGiftCardPage from 'pages/GiftCard/AddGiftCard/AddGiftCard.page';
import UpdateGiftCardPage from 'pages/GiftCard/UpdateGiftCard/UpdateGiftCard.page';
import HomePage from 'pages/home/Home.page';

import './App.css';

/* ============================================ Local Components ============================================ */
const ContextWrapper = ({ config, children }: TContextWrapper) => {
  return (
    <ConfigContextProvider config={config}>
      <LoggerContextProvider values={[config.logger.endpoint, config.appName, config.logger.level]}>
        <AppContextProvider>{children}</AppContextProvider>
      </LoggerContextProvider>
    </ConfigContextProvider>
  );
};

const RouterApp = () => {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/add" element={<AddGiftCardPage />} />
      <Route path="/edit" element={<UpdateGiftCardPage />} />
    </Routes>
  );
};

/* ============================================ Main Component ============================================ */
const App: TApp = ({ config }) => {
  useEffect(() => {
    track.ready(true);
  }, []);

  return (
    <Router basename={config.appBasePath} future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <SnackbarProvider maxSnack={3}>
        <ContextWrapper config={config}>
          <AppLayout>
            <RouterApp />
          </AppLayout>
        </ContextWrapper>
      </SnackbarProvider>
    </Router>
  );
};

export default App;

/* ============================================ Local Types ============================================ */
type TContextWrapper = {
  config: TConfig;
  children: ReactElement;
};

type TApp = (props: { config: TConfig }) => ReactElement;
